import React, { useCallback, useMemo, useState } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import * as ReactGoogleMaps from '@vis.gl/react-google-maps';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { InfoWindow } from '@vis.gl/react-google-maps';
import { GeolocationMarker } from 'libs/end-to-end/src/custom-viz/GeolocationTypes';
import { GeolocationOutput } from 'libs/end-to-end/src/custom-viz/GeolocationTypes';
import { GeolocationRequest } from 'libs/end-to-end/src/custom-viz/GeolocationTypes';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { ProgressSpinner } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressPageInterface';
import { GoogleMapFailure } from 'apps/sigma2-ui/src/components/GoogleMapFailure';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { GoogleMapNoData } from 'apps/sigma2-ui/src/explore-section/components/GoogleMapNoData';
import { Language } from '@mui/icons-material';

// Google Maps API key from environment variable
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  'AIzaSyBwNrqx7QugiT_O-1vmYOSwxMTjhdB2qH0';
const GOOGLE_MAPS_MAP_ID =
  process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || '53ace19d79e119cc78b7429f';

const mapOptions = {
  zoom: 12,
  scrollwheel: false,
  disableDefaultUI: true,
  zoomControl: true,
  zoomControlOptions: {
    position: 7, // BOTTOM_RIGHT
  },
  styles: [],
};

const getCoordinatesCenter = (markers: GeolocationMarker[]) => {
  return {
    lat:
      markers.reduce(
        (acc, marker) => acc + Number(marker.coordinates.split(',')[0]),
        0,
      ) / markers.length,
    lng:
      markers.reduce(
        (acc, marker) => acc + Number(marker.coordinates.split(',')[1]),
        0,
      ) / markers.length,
  };
};

interface GoogleMapInnerProps {
  markers: GeolocationMarker[];
}

const CustomMarker: React.FC<{
  position: { lat: number; lng: number };
  onClick: () => void;
}> = ({ position, onClick }) => (
  <AdvancedMarker position={position} onClick={onClick}>
    <div
      style={{
        width: '14px',
        height: '14px',
        backgroundColor: '#1976d2',
        borderRadius: '50%',
        border: '2px solid white',
        boxShadow: '0 0 2px #1976d2',
        cursor: 'pointer',
      }}
    />
  </AdvancedMarker>
);

const MarkerTooltip: React.FC<{ marker: GeolocationMarker }> = ({ marker }) => {
  const language = useLanguage();

  const { tooltipHeader, tooltipLines } = useMemo(() => {
    const lines = marker.tooltipContent.split('\n');
    const tooltipHeader = lines[0] || language.noDataAvailable();
    const tooltipLines = lines.slice(1);
    return { tooltipHeader, tooltipLines };
  }, [marker, language]);

  return (
    <div className="bg-white text-black w-150px max-h-200px overflow-auto">
      <div className="bg-blue-500 text-white">
        <h5 className="p-1.5 m-0">{tooltipHeader}</h5>
      </div>
      <div className="border-b-1 border-solid border-gray-500">
        {tooltipLines.map((line, index) => (
          <div key={index} className="text-left p-1.5">
            <span>{line}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const GoogleMapInner: React.FC<GoogleMapInnerProps> = ({ markers }) => {
  const [selectedMarker, setSelectedMarker] =
    useState<GeolocationMarker | null>(null);

  const customMarkers = useMemo(() => {
    return markers.map((item, index) => {
      const [lat, lon] = item.coordinates.split(',').map(Number);
      return (
        <CustomMarker
          key={index}
          position={{ lat, lng: lon }}
          onClick={() => setSelectedMarker(item)}
        />
      );
    });
  }, [markers]);

  const getLatLng = useCallback((marker: GeolocationMarker) => {
    return {
      lat: Number(marker.coordinates.split(',')[0]),
      lng: Number(marker.coordinates.split(',')[1]),
    };
  }, []);

  const coordinatesCenter = useMemo(() => {
    return getCoordinatesCenter(markers);
  }, [markers]);

  const language = useLanguage();

  if (!GOOGLE_MAPS_API_KEY || !GOOGLE_MAPS_MAP_ID) {
    return <span>{language.googleMapMissingKey()}</span>;
  }

  return (
    <div className="w-full h-[500px]">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <ReactGoogleMaps.Map
          mapId={GOOGLE_MAPS_MAP_ID}
          style={{ width: '100%', height: '100%' }}
          defaultZoom={mapOptions.zoom}
          defaultCenter={coordinatesCenter}
          gestureHandling={'cooperative'}
          disableDefaultUI={mapOptions.disableDefaultUI}
          zoomControl={mapOptions.zoomControl}
        >
          {customMarkers}
          {selectedMarker !== null && (
            <InfoWindow
              position={{
                lat: getLatLng(selectedMarker).lat,
                lng: getLatLng(selectedMarker).lng,
              }}
              onCloseClick={() => setSelectedMarker(null)}
              pixelOffset={[0, -20]}
            >
              <MarkerTooltip marker={selectedMarker} />
            </InfoWindow>
          )}
        </ReactGoogleMaps.Map>
      </APIProvider>
    </div>
  );
};

export const GoogleMapComponent: React.FC<{
  customVizRequest: GeolocationRequest;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
}> = ({ result, status }) => {
  const { data, hasFailed, isLoading, readyToDisplayDynamicContent } =
    useMemo(() => {
      return parseCustomVizResult({
        result,
        status,
        ResultZodType: GeolocationOutput,
      });
    }, [result, status]);

  if (hasFailed) {
    return <GoogleMapFailure />;
  }

  if (isLoading || !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        w="full"
        h="full"
        flex="1"
      >
        <ProgressSpinner isLoading={isLoading} />
      </Box>
    );
  }

  if (data.markers.length === 0) {
    return <GoogleMapNoData />;
  }

  return (
    <div className="min-h-[500px] w-full">
      {readyToDisplayDynamicContent && data && (
        <GoogleMapInner markers={data.markers} />
      )}
    </div>
  );
};

// This is a static image of the map that is used for reports
export const StaticGoogleMapComponent: React.FC<GoogleMapInnerProps> = ({
  markers,
}) => {
  const coordinatesCenter = useMemo(() => {
    return getCoordinatesCenter(markers);
  }, [markers]);

  const markersString = useMemo(
    () =>
      markers
        .slice(0, 200)
        .map((marker) => `&markers=color:blue|size:tiny|${marker.coordinates}`)
        .join(''),
    [markers],
  );

  const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${coordinatesCenter.lat},${coordinatesCenter.lng}&zoom=12&size=736x400&maptype=roadmap&key=${GOOGLE_MAPS_API_KEY}${markersString}`;

  return (
    <img
      src={staticMapUrl}
      alt="Map"
      style={{ width: '100%', height: '100%' }}
    />
  );
};
